import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileText } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from "../components/ui/table";
import { VoucherDialog } from "../components/VoucherDialog";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const firstOfMonth = () => { const d = new Date(); d.setDate(1); return ymd(d); };

export default function Returns() {
  const { t, fmt, lang } = useI18n();
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(ymd(new Date()));
  const [data, setData] = useState({ rows: [], items: [] });
  const [voucher, setVoucher] = useState(null);

  const range = () => ({
    start: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
    end: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
  });
  const load = useCallback(() => {
    api.get("/reports/returns", { params: range() }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [from, to]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportExcel = async () => {
    try {
      const { data: blob } = await api.get("/reports/returns/export", { params: { ...range(), lang, from_label: from, to_label: to }, responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `returns_${from || "all"}_${to || "all"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openVoucher = async (i) => {
    try { const { data: car } = await api.get(`/cars/${i.car_id}`); setVoucher({ car, voucher: car.vouchers.find((v) => v.number === i.number) }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const sum = (k) => data.rows.reduce((a, r) => a + r[k], 0);

  return (
    <div className="space-y-6">
      <h1 className="text-4xl sm:text-5xl font-extrabold">{t("returnsReport")}</h1>
      <div className="flex flex-wrap items-end gap-3 bg-white border rounded-lg p-4 w-fit">
        <div className="space-y-1.5"><Label>{t("fromDate")}</Label><Input type="date" dir="ltr" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="returns-from-input" /></div>
        <div className="space-y-1.5"><Label>{t("toDate")}</Label><Input type="date" dir="ltr" value={to} onChange={(e) => setTo(e.target.value)} data-testid="returns-to-input" /></div>
        <Button onClick={load} data-testid="returns-apply-btn">{t("searchBtn")}</Button>
        <Button onClick={exportExcel} data-testid="returns-export-btn" className="bg-[#F5BE25] hover:bg-[#E5AC12] text-[#3E3E40]"><Download className="w-4 h-4" />{t("exportExcel")}</Button>
      </div>
      <div className="bg-white border rounded-lg overflow-x-auto max-w-4xl">
        <Table>
          <TableHeader><TableRow>{["supplier", "returnVouchers", "carsReturned", "partsReturned"].map((h) => <TableHead key={h} className="text-start">{t(h)}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {data.rows.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-10" data-testid="returns-empty">{t("noData")}</TableCell></TableRow>}
            {data.rows.map((r) => (
              <TableRow key={r.supplier_name} data-testid={`returns-row-${r.supplier_name}`}>
                <TableCell className="font-bold">{r.supplier_name}</TableCell>
                <TableCell data-testid={`returns-count-${r.supplier_name}`}>{r.returns}</TableCell>
                <TableCell>{r.cars}</TableCell>
                <TableCell>{r.parts_qty}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          {data.rows.length > 0 && (
            <TableFooter><TableRow>
              <TableCell className="font-extrabold">{t("totalRow")}</TableCell>
              <TableCell className="font-extrabold" data-testid="returns-total">{sum("returns")}</TableCell>
              <TableCell className="font-extrabold">{sum("cars")}</TableCell>
              <TableCell className="font-extrabold">{sum("parts_qty")}</TableCell>
            </TableRow></TableFooter>
          )}
        </Table>
      </div>
      {data.items.length > 0 && (
        <div className="bg-white border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader><TableRow>{["voucherNo", "date", "plate", "supplier", "voucherType", "parts", "reasonCol", "employeeName"].map((h) => <TableHead key={h} className="text-start">{t(h)}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {data.items.map((i) => (
                <TableRow key={i.number} data-testid={`return-item-${i.number}`}>
                  <TableCell><Button size="sm" variant="outline" data-testid={`return-voucher-${i.number}`} onClick={() => openVoucher(i)}><FileText className="w-4 h-4" />{i.number}</Button></TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{fmt(i.date)}</TableCell>
                  <TableCell><span className="plate text-xs">{i.plate_number}</span></TableCell>
                  <TableCell>{i.supplier_name}</TableCell>
                  <TableCell className="text-sm">{i.includes_car ? t("returnModeAll") : t("returnPartsOnly")}</TableCell>
                  <TableCell className="text-sm">{i.parts.map((p) => `${p.name}${p.part_number ? ` #${p.part_number}` : ""} × ${p.quantity}`).join("، ") || "-"}</TableCell>
                  <TableCell className="text-sm">{i.reason || "-"}</TableCell>
                  <TableCell className="text-sm">{i.by_name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <VoucherDialog data={voucher} onOpenChange={(o) => !o && setVoucher(null)} />
    </div>
  );
}
